import { useCallback } from 'react';
import { LAYER, useSmartClosePopups } from 'js/components/common/PopupManager';
import { useConfirmationPopup } from 'js/components/common/popups/ConfirmationPopup';

import * as pas from '../../../pas';
import { useLogout } from '../../useLogout';
import { VALIDATION_TYPE } from '../constants';
import { useContinueLogin } from './useContinueLogin';
import { useValidationManager } from './useValidationManager';

export const useValidateFitToPlay = (validationsRef) => {
    const logout = useLogout();
    const continueLogin = useContinueLogin();
    const closePopups = useSmartClosePopups();
    const showConfirmationPopup = useConfirmationPopup();
    const {
        validationStart, validationDone, isValidationInProcess, areAllValidationsDone
    } = useValidationManager(VALIDATION_TYPE.FIT_TO_PLAY, validationsRef);

    const sendFitToPay = useCallback(({ fitToPlay, credentials, afterRegisterLogin, ...options }) => {
        (async () => {
            try {
                await pas.validateFitToPlayToken(fitToPlay);
                validationDone();

                if (areAllValidationsDone()) {
                    await continueLogin({ credentials, afterRegisterLogin, ...options });
                }
            } catch {
                await logout();
            }
        })();
    }, [continueLogin, logout, validationDone, areAllValidationsDone]);

    return useCallback((pasLoginResponse, credentials, options = {}) => {
        const { sessionValidationData = {} } = pasLoginResponse?.data || {};
        const { fitToPlay, ...restOptions } = options;

        if (!sessionValidationData.SessionValidationByFitToPlayData) {
            validationDone();

            return;
        }

        if (isValidationInProcess()) {
            return;
        }

        validationStart();

        if (options.fitToPlay) {
            sendFitToPay({ credentials, fitToPlay, ...restOptions });
        } else {
            showConfirmationPopup({
                isModal: true,
                id: VALIDATION_TYPE.FIT_TO_PLAY,
                layer: LAYER.SESSION_VALIDATION,
                title: 'playtech.mobile.system.popup.fitToPlay.title',
                content: 'playtech.mobile.system.popup.fitToPlay.text',
                acceptBtnText: 'playtech.mobile.system.message.confirm',
                declineBtnText: 'playtech.mobile.system.message.decline',
                onAccept: () => sendFitToPay({
                    fitToPlay: true,
                    credentials,
                    ...restOptions
                }),
                onDecline: () => {
                    sendFitToPay({
                        fitToPlay: false,
                        credentials,
                        ...restOptions
                    });
                    closePopups({ excludeLayers: [LAYER.LOGIN] });
                }
            });
        }
    }, [closePopups, showConfirmationPopup, sendFitToPay, validationStart, validationDone, isValidationInProcess]);
};
